import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { authApi } from '@/api/resources/auth'
import { useSession } from './SessionProvider'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * Re-authentication gate (§5.3). Sensitive endpoints return 403 REAUTH_REQUIRED
 * when the JWT's auth_time is stale. In the BFF model, fresh auth means a
 * `prompt=login` round-trip through Keycloak that returns to the interrupted
 * screen; the screen then re-initiates its action.
 */
interface ReAuthContextValue {
  /** Opens the confirm-identity dialog. Confirming navigates into the re-auth flow. */
  promptReauth: () => void
}

const ReAuthContext = createContext<ReAuthContextValue | null>(null)

export function ReAuthProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const { user } = useSession()
  const { t } = useTranslation()

  const promptReauth = useCallback(() => setOpen(true), [])

  const confirm = useCallback(() => {
    const returnTo = location.pathname + location.search
    window.location.assign(authApi.loginUrl(returnTo, { promptLogin: true }))
  }, [location])

  const value = useMemo(() => ({ promptReauth }), [promptReauth])

  return (
    <ReAuthContext.Provider value={value}>
      {children}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('reauth.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('reauth.description', { email: user?.email ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirm}>{t('reauth.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ReAuthContext.Provider>
  )
}

export function useReauth(): ReAuthContextValue {
  const context = useContext(ReAuthContext)
  if (!context) throw new Error('useReauth must be used within <ReAuthProvider>')
  return context
}
